import { z } from 'zod';
import { zPlainDate } from '@/shared/lib/datetime/datetime.zod';

/** Allowed priority values, single source for the schema and the UI options. */
export const AI_PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;
export type AiPriority = (typeof AI_PRIORITIES)[number];

/**
 * Form values for the editable draft inside the AI quick-create dialog.
 * `dueDate` is a plain `yyyy-MM-dd` string (DatePicker contract); the apply
 * payload sends the draft as-is (no response schema, R9).
 */
export const quickTaskDraftSchema = z.object({
  title: z
    .string()
    .min(1, { error: 'tasks:validation.titleRequired' })
    .max(200, { error: 'tasks:validation.titleMax' }),
  description: z.string().nullable().optional(),
  startDate: zPlainDate().nullable().optional(),
  dueDate: zPlainDate().nullable().optional(),
  statusId: z.string().nullable().optional(),
  groupId: z.string().nullable().optional(),
  // Opaque id, not a uuid: project member ids come off the wire as the backend's numeric
  // ids, so a uuid check rejected every assignee the proposal ever filled in — and the
  // form swallowed the failed submit. Membership is the backend's call anyway (QTC-11).
  assigneeId: z.string().nullable().optional(),
  priority: z.enum(AI_PRIORITIES).nullable().optional(),
  estimatedMinutes: z.number().int().min(0).nullable().optional(),
  tags: z.array(z.string()).optional(),
});

export type QuickTaskDraftFormValues = z.infer<typeof quickTaskDraftSchema>;

export const quickTaskDraftDefaultValues: QuickTaskDraftFormValues = {
  title: '',
  description: null,
  startDate: null,
  dueDate: null,
  statusId: null,
  groupId: null,
  assigneeId: null,
  priority: null,
  estimatedMinutes: null,
  tags: [],
};

// --- Task Plan (goal → multiple tasks) ---

/** Goal input validation: 1..500 characters. */
export const goalSchema = z.object({
  goal: z
    .string()
    .min(1, { error: 'ai:validation.goalRequired' })
    .max(500, { error: 'ai:validation.goalMax' }),
});

export type GoalFormValues = z.infer<typeof goalSchema>;

/** Per-row draft inside the task plan proposal list. */
export const taskPlanDraftSchema = z.object({
  title: z
    .string()
    .min(1, { error: 'ai:validation.taskTitleRequired' })
    .max(200, { error: 'ai:validation.taskTitleMax' }),
  priority: z.number().min(1).max(4).nullable().optional(),
  estimatedMinutes: z.number().min(0).nullable().optional(),
  selected: z.boolean(),
});

export type TaskPlanDraftFormValues = z.infer<typeof taskPlanDraftSchema>;
